import * as amplitude from "@amplitude/analytics-browser";
import {
  CloseButton,
  Flex,
  Icon,
  IconButton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";
import {
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiFileTextFill,
  RiFileTextLine,
} from "react-icons/ri";
import { TextWithOverflowTooltip } from "src/components/TextWithOverflowTooltip";
import { Document } from "./types";

export type DocumentViewerHighlightToggleProps = {
  show: boolean;
  isDisabled: boolean;
  highlightsOn: boolean;
  onToggle: () => void;
};

type Props = {
  currentDocumentIndex: number;
  onClose?: () => void;
  document: Document | Document[];
  setCurrentDocumentIndex: (index: number) => void;
  highlightToggle?: DocumentViewerHighlightToggleProps;
};
export const DocumentViewerHeader = ({
  currentDocumentIndex,
  onClose,
  document,
  setCurrentDocumentIndex,
  highlightToggle,
}: Props) => {
  const documentCount = Array.isArray(document) ? document.length : 1;

  const currentDocument = React.useMemo(() => {
    if (Array.isArray(document)) {
      return document[currentDocumentIndex];
    }

    return document;
  }, [currentDocumentIndex, document]);

  return (
    <Flex
      width="100%"
      bg="gray.600"
      paddingX="6"
      paddingY="4"
      color="white"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        "@container (width < 450px)": {
          padding: "2",
        },
      }}
      flexWrap="nowrap"
    >
      <Flex alignItems="center" gap="2" overflow="auto">
        <Icon as={RiFileTextLine} />
        <TextWithOverflowTooltip content={currentDocument?.fileName ?? ""} />
      </Flex>
      <Flex alignItems="center">
        {highlightToggle?.show && (
          <Tooltip
            label={
              highlightToggle.isDisabled
                ? "No highlights to show"
                : highlightToggle.highlightsOn
                ? "Hide highlights"
                : "Show highlights"
            }
            hasArrow
          >
            <IconButton
              isDisabled={highlightToggle.isDisabled}
              onClick={() => highlightToggle.onToggle()}
              icon={
                <Icon
                  as={
                    highlightToggle.highlightsOn
                      ? RiFileTextFill
                      : RiFileTextLine
                  }
                />
              }
              aria-label={
                highlightToggle.isDisabled
                  ? "No matches to highlight"
                  : highlightToggle.highlightsOn
                  ? "Hide highlights"
                  : "Show highlights"
              }
              variant="ghost"
              colorScheme="whiteAlpha"
              color="whiteAlpha.800"
            />
          </Tooltip>
        )}
        <IconButton
          isDisabled={documentCount <= 1}
          onClick={() => {
            const newIndex =
              (currentDocumentIndex - 1 + documentCount) % documentCount;
            setCurrentDocumentIndex(newIndex);
            amplitude.track("File changed", {
              file_name: Array.isArray(document)
                ? document[newIndex]?.fileName
                : undefined,
            });
          }}
          icon={<RiArrowLeftSLine />}
          aria-label="Previous document"
          variant="ghost"
          colorScheme="whiteAlpha"
        />
        <Text whiteSpace="nowrap" textAlign="center">
          {`${currentDocumentIndex + 1} of ${documentCount} ${
            documentCount > 1 ? "files" : "file"
          }`}
        </Text>
        <IconButton
          isDisabled={documentCount <= 1}
          onClick={() => {
            const newIndex = (currentDocumentIndex + 1) % documentCount;
            setCurrentDocumentIndex(newIndex);
            amplitude.track("File changed", {
              file_name: Array.isArray(document)
                ? document[newIndex]?.fileName
                : undefined,
            });
          }}
          icon={<RiArrowRightSLine />}
          aria-label="Next document"
          variant="ghost"
          colorScheme="whiteAlpha"
        />
      </Flex>
      {onClose !== undefined ? (
        <CloseButton
          onClick={() => {
            amplitude.track("Preview closed");
            onClose();
          }}
          sx={{
            "@container (width < 450px)": {
              minWidth: "4",
              minHeight: "4",
            },
          }}
        />
      ) : null}
    </Flex>
  );
};
